import './style.scss';

export function team_members(root = document) {
  root.querySelectorAll('.team-members').forEach(block => {
    const artwork = block.querySelector('.team-members__artwork');
    if (!artwork || block.dataset.artworkReady || document.body.classList.contains('block-editor-page')) return;
    block.dataset.artworkReady = 'true';
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    block.classList.add('has-artwork-reveal');
    const reveal = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      block.classList.add('is-artwork-visible');
      reveal.disconnect();
    }, { threshold: .15 });
    reveal.observe(artwork);
  });
  const { Swiper, SwiperModules = {} } = window.fbsThemeVendors || {};
  if (!Swiper || document.body.classList.contains('block-editor-page')) return;
  root.querySelectorAll('[data-team-members]').forEach((block) => {
    const slider = block.querySelector('[data-team-slider]');
    const originals = [...block.querySelectorAll('[data-team-member]')];
    const memberCount = originals.length;
    const slides = [...originals];
    if (!slider || slides.length < 2 || block.dataset.teamReady) return;
    block.dataset.teamReady = 'true';
    // Swiper's centered auto-width loop needs more physical slides than the
    // three visible portraits. Duplicate only the DOM, never the CMS records.
    const loop = memberCount >= 3;
    if (loop) {
      const track = slider.querySelector('.swiper-wrapper');
      originals.forEach((slide, index) => {
        const copy = slide.cloneNode(true);
        const profile = copy.querySelector('[data-team-profile]');
        profile.id += `-loop-${index}`;
        copy.querySelector('[data-select-member]').setAttribute('aria-controls', profile.id);
        track.append(copy);
        slides.push(copy);
      });
    }
    slides.forEach(slide => { slide.querySelector('[data-select-member]').disabled = false; });
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const speed = reduce.matches ? 0 : Number(block.dataset.transitionSpeed) || 700;
    const wantsAutoplay = block.dataset.autoplay === 'true' && !reduce.matches;
    const controls = block.querySelector('[data-team-controls]');
    const status = block.querySelector('[data-team-status]');
    const pause = block.querySelector('[data-team-pause]');
    let userPaused = false;
    let announce = false;
    let announcementTimer;
    const announceSelection = (instance) => {
      if (!announce || !status) return;
      window.clearTimeout(announcementTimer);
      announcementTimer = window.setTimeout(() => {
        if (instance.destroyed) return;
        const active = instance.slides[instance.activeIndex];
        const member = Number(active.querySelector('[data-select-member]').dataset.selectMember) + 1;
        status.textContent = `${member} / ${memberCount}: ${active.dataset.memberLabel}`;
        announce = false;
      }, instance.params.speed + 80);
    };
    block.classList.add('is-enhanced');
    block.style.setProperty('--team-transition', `${speed}ms`);
    const update = (swiper) => {
      slides.forEach((slide, index) => {
        const active = slide === swiper.slides[swiper.activeIndex];
        slide.classList.toggle('is-selected', active);
        slide.inert = Math.abs(swiper.slides.indexOf(slide) - swiper.activeIndex) > 1;
        const profile = slide.querySelector('[data-team-profile]');
        profile.setAttribute('aria-hidden', active ? 'false' : 'true');
        profile.toggleAttribute('inert', !active);
        slide.querySelector('[data-select-member]').setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      announceSelection(swiper);
    };
    const swiper = new Swiper(slider, {
      modules: [SwiperModules.A11y, SwiperModules.Keyboard, SwiperModules.Autoplay].filter(Boolean),
      slidesPerView: 'auto', centeredSlides: true, spaceBetween: 16,
      // Portraits are native buttons, but should still allow a drag gesture.
      focusableElements: 'input, select, option, textarea, video, label',
      touchStartPreventDefault: false,
      initialSlide: Math.min(memberCount - 1, Number(block.dataset.initialMember) || 0),
      speed, loop, rewind: !loop, grabCursor: true, watchSlidesProgress: true,
      keyboard: { enabled: true, onlyInViewport: true, pageUpDown: false },
      a11y: { enabled: true, slideRole: 'group', slideLabelMessage: null },
      autoplay: wantsAutoplay ? { delay: Number(block.dataset.autoplayDelay) || 6500, disableOnInteraction: false, pauseOnMouseEnter: true } : false,
      on: {
        init: update,
        slideChange: update,
        touchEnd(instance) { announceSelection(instance); },
        sliderFirstMove() { announce = true; },
      },
    });
    if (controls) controls.hidden = false;
    block.querySelector('[data-team-previous]')?.addEventListener('click', () => { announce = true; swiper.slidePrev(); });
    block.querySelector('[data-team-next]')?.addEventListener('click', () => { announce = true; swiper.slideNext(); });
    block.querySelectorAll('[data-select-member]').forEach((button) => {
      button.addEventListener('click', () => {
        announce = true;
        const slide = button.closest('[data-team-member]');
        if (loop) swiper.slideToLoop(Number(slide.dataset.swiperSlideIndex));
        else swiper.slideTo(Number(button.dataset.selectMember));
      });
    });
    slider.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') announce = true;
    });
    const syncAutoplay = () => {
      if (!wantsAutoplay || !swiper.autoplay) return;
      const focused = block.contains(document.activeElement);
      const hovered = slider.matches(':hover');
      if (userPaused || document.hidden || focused || hovered || reduce.matches) swiper.autoplay.pause();
      else swiper.autoplay.resume();
    };
    block.addEventListener('focusin', syncAutoplay);
    block.addEventListener('focusout', () => requestAnimationFrame(syncAutoplay));
    document.addEventListener('visibilitychange', syncAutoplay);
    pause?.addEventListener('click', () => {
      userPaused = !userPaused;
      pause.setAttribute('aria-pressed', String(userPaused));
      pause.textContent = userPaused ? 'Resume automatic rotation' : 'Pause automatic rotation';
      syncAutoplay();
    });
    reduce.addEventListener('change', () => {
      swiper.params.speed = reduce.matches ? 0 : Number(block.dataset.transitionSpeed) || 700;
      block.style.setProperty('--team-transition', `${swiper.params.speed}ms`);
      syncAutoplay();
    });
    const observer = new IntersectionObserver(([entry]) => {
      if (wantsAutoplay && swiper.autoplay) entry.isIntersecting ? syncAutoplay() : swiper.autoplay.pause();
    });
    observer.observe(slider);
    swiper.on('setTranslate', () => slides.forEach((slide, index) => { slide.inert = Math.abs(swiper.slides.indexOf(slide) - swiper.activeIndex) > 1; }));
  });
}
